import { createContext, useContext, useId, useState, Children, type CSSProperties } from "react";
import { motion, useReducedMotion, type Transition } from "framer-motion";
import { twMerge } from "tailwind-merge";
import { Button } from "./Button";

const REVEAL: Transition = { type: "spring", duration: 0.45, bounce: 0.28 };
const INSTANTANEO: Transition = { duration: 0 };
const POR_LINHA = 4;

type ContextType = {
    value: string | number;
    onChange: (value: string | number) => void;
    layoutId: string;
    indicatorStyle?: CSSProperties;
    trocouDeLinha: boolean;
    setTrocouDeLinha: (trocou: boolean) => void;
    quantidade: number;
};

const ButtonControlsContext = createContext<ContextType | null>(null);

const useButtonControls = () => {
    const ctx = useContext(ButtonControlsContext);
    if (!ctx) {
        throw new Error("ButtonControls components must be used inside ButtonControls");
    }
    return ctx;
};

type ButtonControlsProps = {
    value: string | number;
    onChange: (value: string | number) => void;
    children: React.ReactNode;
    className?: string;
    indicatorStyle?: CSSProperties;
};

const ButtonControlsRoot = ({
    value,
    onChange,
    children,
    className,
    indicatorStyle,
}: ButtonControlsProps) => {
    const layoutId = useId();
    const [trocouDeLinha, setTrocouDeLinha] = useState(false);
    const quantidade = Children.count(children);

    return (
        <ButtonControlsContext.Provider value={{ value, onChange, layoutId, indicatorStyle, trocouDeLinha, setTrocouDeLinha, quantidade }}>
            <div
                className={twMerge(`relative flex rounded-lg bg-gray-100 p-1 ${quantidade > POR_LINHA ? 'flex-wrap' : ''}`, className)}>
                {children}
            </div>
        </ButtonControlsContext.Provider>
    );
};

type ItemProps = {
    value: string | number;
    children: React.ReactNode;
    label?: React.ReactNode;
    className?: string;
    skipAnimation?: boolean;
};

const ButtonControlsItem = ({
    value,
    children,
    label,
    className,
    skipAnimation = false
}: ItemProps) => {
    const { value: activeValue, onChange, layoutId, indicatorStyle, trocouDeLinha, setTrocouDeLinha, quantidade } = useButtonControls();
    const semMovimento = useReducedMotion();

    const isActive = value === activeValue;
    const transition = skipAnimation || semMovimento ? INSTANTANEO : REVEAL;

    const aoClicar = (e: React.MouseEvent<HTMLButtonElement>) => {
        const alvo = e.currentTarget;
        const ativo = alvo.parentElement?.querySelector<HTMLElement>('[data-ativo="true"]');

        setTrocouDeLinha(Boolean(ativo) && ativo!.offsetTop !== alvo.offsetTop);
        onChange(value);
    };

    return (
        <Button
            onClick={aoClicar}
            data-ativo={isActive ? "true" : undefined}
            className={twMerge(
                "relative flex flex-1 items-center justify-center rounded-lg px-2 py-1 transition-colors bg-transparent [&_svg]:!size-5 [&_svg]:!z-10",
                quantidade < POR_LINHA ? "min-w-16" : "",
                quantidade > POR_LINHA ? "flex-[1_1_25%]" : "",
                isActive ? "text-gray-900 font-medium" : "text-gray-500 font-normal hover:text-gray-700",
                className)} >

            <motion.span layout="position" transition={transition} className="relative z-10 flex items-center">
                {children}

                {label !== undefined && (
                    <motion.span
                        transition={transition}
                        initial={false}
                        animate={{
                            clipPath: isActive ? "inset(0% 0% 0% 0%)" : "inset(0% 50% 0% 50%)",
                            opacity: isActive ? 1 : 0
                        }}
                        className={twMerge("overflow-hidden whitespace-nowrap", isActive ? "w-auto" : "w-0")}
                    >
                        <span className="block pl-1.5">{label}</span>
                    </motion.span>
                )}
            </motion.span>

            {isActive && (
                <motion.div
                    layoutId={layoutId}
                    style={indicatorStyle}
                    className="absolute inset-0 rounded-lg bg-white border border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.10)]"
                    transition={trocouDeLinha ? INSTANTANEO : transition} />
            )}
        </Button>
    );
};

export const ButtonControls = Object.assign(ButtonControlsRoot, {
    Item: ButtonControlsItem,
});
